/**
 * Help Feedback Component
 *
 * Purpose: Thumbs up/down feedback for help articles
 * Created: March 24, 2026
 */

import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@xanectech/ui/components';
import { HelpService } from '@xanectech/data';
import { useAuth } from '@xanectech/auth';

interface HelpFeedbackProps {
  articleId: string;
}

export const HelpFeedback: React.FC<HelpFeedbackProps> = ({ articleId }) => {
  const { user } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFeedback = async (isHelpful: boolean) => {
    if (submitted || submitting) return;
    setSubmitting(true);

    const success = await HelpService.submitFeedback(
      articleId,
      isHelpful,
      user?.id,
    );

    if (success) {
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground py-2'>
        <span>Thanks for your feedback!</span>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-3 py-2'>
      <span className='text-sm text-muted-foreground'>Was this helpful?</span>
      <div className='flex gap-1'>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => handleFeedback(true)}
          disabled={submitting}
          className='h-8 w-8 p-0'
        >
          <ThumbsUp size={14} />
        </Button>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => handleFeedback(false)}
          disabled={submitting}
          className='h-8 w-8 p-0'
        >
          <ThumbsDown size={14} />
        </Button>
      </div>
    </div>
  );
};
